import { useCallback, useEffect } from 'react';
import cn from 'classnames';

import Permitted from 'components/core/Permitted.component';
import Space from 'components/generic/Space.component';
import TruncatedText from 'components/generic/TruncatedText.component';
import PERM from 'constants/core/perm.const';
import { useAccessIds } from 'providers/Access.provider';
import { useTranslation } from 'providers/GILT.provider';
import noNilArray from 'utils/arrays/noNilArray.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import call from 'utils/functions/call.util';
import logger from 'utils/logger.util';
import { isValidId } from 'utils/predicates/fields.util';

import StylistList from 'cashup/StylistList.component';

const L = logger('CashupStaffPicker'); // eslint-disable-line no-unused-vars

const CashupStaffPicker = ({ disabled, isEmployed, isNotAllStaff, onClick, onSelect, isDropdownOpen, stylists, value, setPersonalStylistName }) => {
  const { accessStylistId } = useAccessIds();
  const { t } = useTranslation();

  const handleSelect = useCallback(
    (ev) => {
      if (disabled) return;
      call('CashupStaffPicker.onSelect', onSelect, ev);
      call('CashupStaffPicker.onClick', onClick);
    },
    [disabled, onClick, onSelect],
  );

  const stylist = noNilArray(stylists).find((s) => s.id === accessStylistId);
  const stylistName = isValidId(accessStylistId) ? fromAnyToTrimmed(stylist?.user_name) || t('cashUp.staffPicker.noName') : t('cashUp.staffPicker.noStylist');

  L.debug('()', accessStylistId, stylistName);

  useEffect(() => {
    if (setPersonalStylistName) {
      setPersonalStylistName(stylistName);
    }
  }, [stylistName, setPersonalStylistName]);

  return (
    <div
      data-bem="CashupStaffPicker"
      className={cn({
        'select-box-status-bar': true,
        'filter-in-used': isNotAllStaff,
      })}
    >
      <div data-bem="CashupStaffPicker__box" className="select-box--box">
        {isNotAllStaff && (
          <span data-bem="CashupStaffPicker__message" className="select-box--message">
            {t('cashUp.staffPicker.filterInUse')}
          </span>
        )}

        <div
          data-bem="CashupStaffPicker__dropdown"
          className={cn({
            'select-box--container': true,
            'is-open': isDropdownOpen,
          })}
          onClick={onClick}
          role="button"
          tabIndex={0}
        >
          <span data-bem="CashupStaffPicker__label" className="select-box--name">
            {t('cashUp.staffPicker.staff')}
          </span>

          <div data-bem="CashupStaffPicker__item" className="select-box--selected-item">
            <Space>
              <Permitted value={PERM.canViewDailyCashupAllStaff}>
                <TruncatedText max={24} suffix="..." value={value} />
              </Permitted>
              <Permitted value={PERM.canViewDailyCashupPersonal}>
                <TruncatedText max={24} suffix="..." value={stylistName} />
              </Permitted>
            </Space>
            <Space>{isEmployed}</Space>
          </div>

          <Permitted value={PERM.canViewDailyCashupAllStaff}>
            {!disabled && (
              <div data-bem="CashupStaffPicker__arrow" className="select-box--arrow">
                <span
                  className={cn({
                    'arrow-up': isDropdownOpen,
                    'arrow-down': !isDropdownOpen,
                  })}
                />
              </div>
            )}
          </Permitted>
        </div>

        <Permitted value={PERM.canViewDailyCashupAllStaff}>
          <div
            data-bem="CashupStaffPicker__list"
            className={cn({
              A: isDropdownOpen,
              B: !isDropdownOpen,
            })}
          >
            {isDropdownOpen && <StylistList stylists={stylists} onSelect={handleSelect} />}
          </div>
        </Permitted>
      </div>
    </div>
  );
};

export default CashupStaffPicker;
